'use client';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { semanticTokens, themeFallbacks } from '@packages/config/theme';
import React, { useEffect, useRef, useState } from 'react';

/**
 * AppleQR — Apple-quality QR Code Component
 *
 * Uses qr-code-styling library for premium QR generation with:
 * - Rounded dots (Apple style)
 * - Custom corner styles
 * - Logo support (centered)
 * - Gradient capability
 * - Liquid Glass visual integration
 *
 * CANON-COMPLIANT: Uses semantic tokens, respects prefers-reduced-motion
 */

export type QRDotStyle = 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'square' | 'extra-rounded';
export type QRCornerStyle = 'square' | 'dot' | 'extra-rounded';
export type QRVariant = 'default' | 'glass' | 'elevated' | 'minimal';

export interface AppleQRProps {
  /** The data to encode in the QR code */
  value: string;
  /** Size in pixels (default: 200) */
  size?: number;
  /** Logo image URL to display in center (optional) */
  logo?: string;
  /** Logo size ratio relative to QR (default: 0.25) */
  logoRatio?: number;
  /** Dot style (default: 'rounded') */
  dotStyle?: QRDotStyle;
  /** Corner square style (default: 'extra-rounded') */
  cornerStyle?: QRCornerStyle;
  /** Corner dot style (default: 'dot') */
  cornerDotStyle?: QRCornerStyle;
  /** Visual variant (default: 'default') */
  variant?: QRVariant;
  /** Alt text for accessibility */
  alt?: string;
  /** Additional CSS classes */
  className?: string;
  /** Error correction level (default: 'H' for logo support) */
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  /** Quiet zone margin in modules (default: 2) */
  margin?: number;
  /** Enable shimmer effect on hover (Liquid Glass L2) */
  shimmer?: boolean;
  /** Enable lensing effect (Liquid Glass L3) - performance intensive */
  lensing?: boolean;
  /** Called when QR code is ready */
  onReady?: () => void;
  /** Called on generation error */
  onError?: (error: Error) => void;
}

// Get computed theme colors - returns hex colors
const getThemeColors = (): { foreground: string; background: string } => {
  if (typeof window === 'undefined') {
    // SSR fallback: use theme fallbacks from semantic tokens
    return { foreground: themeFallbacks.foreground, background: themeFallbacks.background };
  }

  try {
    const root = getComputedStyle(document.documentElement);
    const fgHsl = root.getPropertyValue('--foreground').trim();
    const bgHsl = root.getPropertyValue('--background').trim();

    // If variables are not set, use theme fallbacks
    if (!fgHsl || !bgHsl) {
      return { foreground: themeFallbacks.foreground, background: themeFallbacks.background };
    }

    // Convert HSL to hex for qr-code-styling
    const hslToHex = (hsl: string): string => {
      try {
        const parts = hsl.split(' ').filter(Boolean);
        if (parts.length < 3) return themeFallbacks.foreground;

        const h = parseFloat(parts[0]) || 0;
        const s = parseFloat(parts[1].replace('%', '')) || 0;
        const l = parseFloat(parts[2].replace('%', '')) || 0;

        const sNorm = s / 100;
        const lNorm = l / 100;

        const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = lNorm - c / 2;

        let r = 0, g = 0, b = 0;

        if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
        else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
        else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
        else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
        else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
        else { r = c; g = 0; b = x; }

        const toHex = (n: number) => {
          const val = Math.round((n + m) * 255);
          return Math.max(0, Math.min(255, val)).toString(16).padStart(2, '0');
        };
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      } catch {
        return themeFallbacks.foreground;
      }
    };

    return {
      foreground: hslToHex(fgHsl),
      background: hslToHex(bgHsl)
    };
  } catch {
    // Fallback: use theme fallbacks from semantic tokens
    return { foreground: themeFallbacks.foreground, background: themeFallbacks.background };
  }
};

export const AppleQR: React.FC<AppleQRProps> = ({
  value,
  size = 200,
  logo,
  logoRatio = 0.25,
  dotStyle = 'rounded',
  cornerStyle = 'extra-rounded',
  cornerDotStyle = 'dot',
  variant = 'default',
  alt = 'QR Code',
  className,
  errorCorrectionLevel = 'H',
  margin = 2,
  shimmer = false,
  lensing = false,
  onReady,
  onError
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    let mounted = true;

    const generateQR = async () => {
      if (!value) {
        setIsLoading(false);
        setError('No value provided');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setQrDataUrl(null);

        const colors = getThemeColors();

        // Try qr-code-styling first
        try {
          const QRCodeStyling = (await import('qr-code-styling')).default;

          const qrCode = new QRCodeStyling({
            width: size,
            height: size,
            type: 'svg',
            data: value,
            margin: margin,
            qrOptions: {
              errorCorrectionLevel: errorCorrectionLevel,
            },
            dotsOptions: {
              color: colors.foreground,
              type: dotStyle,
            },
            cornersSquareOptions: {
              color: colors.foreground,
              type: cornerStyle,
            },
            cornersDotOptions: {
              color: colors.foreground,
              type: cornerDotStyle,
            },
            backgroundOptions: {
              color: variant === 'glass' || variant === 'minimal' ? 'transparent' : colors.background,
            },
            ...(logo && {
              image: logo,
              imageOptions: {
                crossOrigin: 'anonymous',
                margin: Math.round(size * 0.02),
                imageSize: logoRatio,
                hideBackgroundDots: true,
              },
            }),
          });

          // Get as data URL instead of appending to DOM
          const blob = await qrCode.getRawData('svg');
          if (blob && mounted) {
            const svgText = await blob.text();
            const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;
            setQrDataUrl(dataUrl);
            setIsLoading(false);
            onReady?.();
            return;
          }
        } catch (stylingError) {
          console.warn('[AppleQR] qr-code-styling failed, falling back to qrcode:', stylingError);
        }

        // Fallback to basic qrcode library
        const qrcode = await import('qrcode');
        const dataUrl = await qrcode.toDataURL(value, {
          width: size,
          margin: margin,
          color: {
            dark: colors.foreground,
            light: variant === 'glass' || variant === 'minimal' ? 'transparent' : colors.background
          },
          errorCorrectionLevel: errorCorrectionLevel
        });

        if (mounted) {
          setQrDataUrl(dataUrl);
          setIsLoading(false);
          onReady?.();
        }
      } catch (err) {
        if (mounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to generate QR code';
          console.error('[AppleQR] Generation failed:', err);
          setError(errorMessage);
          setIsLoading(false);
          onError?.(err instanceof Error ? err : new Error(errorMessage));
        }
      }
    };

    generateQR();

    return () => {
      mounted = false;
    };
  }, [value, size, logo, logoRatio, dotStyle, cornerStyle, cornerDotStyle, variant, errorCorrectionLevel, margin, onReady, onError]);

  // Variant-specific styles
  const variantStyles = {
    default: cn(
      'rounded-xl border p-4',
      semanticTokens.bg.surface,
      semanticTokens.border.default,
      semanticTokens.shadow.sm
    ),
    glass: cn(
      'rounded-xl border p-4',
      // Liquid Glass Surface tier
      'bg-gradient-to-br from-white/85 via-white/75 to-white/85',
      'dark:from-black/60 dark:via-black/50 dark:to-black/60',
      'backdrop-blur-xl backdrop-saturate-[180%]',
      'dark:backdrop-saturate-150 dark:brightness-90',
      semanticTokens.border.default,
      semanticTokens.shadow.md
    ),
    elevated: cn(
      'rounded-xl border p-4',
      semanticTokens.bg.surface,
      semanticTokens.border.default,
      semanticTokens.shadow.lg,
      'ring-1 ring-black/5 dark:ring-white/10'
    ),
    minimal: cn(
      'rounded-lg p-2'
    )
  };

  const containerSize = size + (variant === 'minimal' ? 16 : 32);

  // Error state
  if (error) {
    return (
      <div
        className={cn(
          'flex items-center justify-center',
          variantStyles[variant],
          className
        )}
        style={{ width: containerSize, height: containerSize }}
        role="img"
        aria-label={`${alt} - Error: ${error}`}
      >
        <span className={cn('text-xs text-center px-2', semanticTokens.fg.muted)}>
          QR code unavailable
        </span>
      </div>
    );
  }

  // Loading state
  if (isLoading || !qrDataUrl) {
    return (
      <Skeleton
        className={cn(
          'flex items-center justify-center',
          variant !== 'minimal' && 'rounded-xl',
          className
        )}
        style={{ width: containerSize, height: containerSize }}
        role="img"
        aria-label={`${alt} - Loading`}
      >
        <span className={cn('text-xs', semanticTokens.fg.muted)}>Generating...</span>
      </Skeleton>
    );
  }

  const showShimmer = shimmer && !prefersReducedMotion && isHovered;

  return (
    <div
      className={cn(
        'relative overflow-hidden transition-transform',
        variantStyles[variant],
        shimmer && !prefersReducedMotion && 'hover:scale-[1.02]',
        semanticTokens.motion.standard,
        className
      )}
      style={{ width: containerSize, height: containerSize }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="img"
      aria-label={alt}
    >
      {/* Shimmer overlay (Liquid Glass L2) */}
      {showShimmer && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background: `linear-gradient(
              135deg,
              transparent 30%,
              rgba(255,255,255,0.4) 50%,
              transparent 70%
            )`,
            backgroundSize: '200% 200%',
            animation: 'shimmer 1.5s ease-in-out infinite',
          }}
        />
      )}

      {/* QR Code image */}
      <img
        src={qrDataUrl}
        alt={alt}
        width={size}
        height={size}
        className="block"
        style={{
          imageRendering: 'crisp-edges',
        }}
      />

      {/* Shimmer keyframes injection */}
      {shimmer && !prefersReducedMotion && (
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 200%; }
            100% { background-position: -200% -200%; }
          }
        `}</style>
      )}
    </div>
  );
};

// Re-export as default for easier imports
export default AppleQR;
